import React, {useEffect, useState, useMemo, useRef, useCallback} from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    CardHeader,
    Row
} from "reactstrap";
import {Link, useParams} from "react-router-dom";
import Loader from "../../../Components/Common/Loader";
import {toast, ToastContainer} from 'react-toastify';
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import DeleteModal from "../../../Components/Common/DeleteModal";
import ExportCSVModal from "../../../Components/Common/ExportCSVModal";
//redux
import {useSelector, useDispatch} from "react-redux";

import {openUnitModal, closeUnitModal} from "../../../slices/modal/reducer";
import UnitModal from "./UnitModal"
import {
    getUnits as onGetUnits,
    addNewUnit as onAddNewUnit,
    updateUnit as onUpdateUnit,
    deleteUnit as onDeleteUnit,
    getBlocks as onGetBlocks,
} from "../../../slices/thunks";
import {useProfile} from "../../../Components/Hooks/UserHooks";


import {FilterContainer} from '../../../Components/filters/FilterContainer';
import {createRangeFilter} from '../../../Components/filters/FilterTypes/RangeFilter';
import {createSelectFilter} from '../../../Components/filters/FilterTypes/SelectFilter';
import {createToggleFilter} from '../../../Components/filters/FilterTypes/ToggleFilter';
import {createSelector} from "reselect";

const selectBlockProperties = createSelector(
    (state) => state.Buildings,
    (build) => ({
        blocks: build.blocks,
        isBlockSuccess: build.isBlockSuccess,
        error: build.error,
        loading: build.loading
    })
);

const selectUnitProperties = createSelector(
    (state) => state.Unit,
    (unit) => ({
        units: unit.units,
        isUnitSuccess: unit.isUnitSuccess,
        error: unit.error,
        loading: unit.loading,
    })
);


const UnitFilters = ({ data, onFilterChange }) => {
    const prevDataRef = useRef(null);
    const prevPriceRangeRef = useRef({ min: null, max: null });

    // Calculate filter configs once based on current data
    const filterConfigs = useMemo(() => {
        if (!data?.length) return [];

        // Calculate unique values and sort them properly
        const uniqueUnitTypes = [...new Set(data.map(unit => unit.unit_type).filter(Boolean))];
        const uniqueAptTypes = [...new Set(data.map(unit => unit.apt_type).filter(Boolean))];
        const uniqueFloors = [...new Set(data.map(unit => Number(unit.floor)).filter(Boolean))].sort((a, b) => a - b);
        const uniqueRooms = [...new Set(data.map(unit => Number(unit.num_rooms)).filter(Boolean))].sort((a, b) => a - b);

        // Calculate price range with safety checks
        const prices = data.map(unit => Number(unit.initial_price) || 0).filter(price => !isNaN(price));
        const minPrice = prices.length ? Math.min(...prices) : 0;
        const maxPrice = prices.length ? Math.max(...prices) : 0;

        // If all prices are the same, add a small range to prevent the slider error
        const adjustedMaxPrice = minPrice === maxPrice ? maxPrice + 1 : maxPrice;


        // Store current price range for future reference
        prevPriceRangeRef.current = { min: minPrice, max: adjustedMaxPrice };

        return [
            createRangeFilter({
                field: 'initial_price',
                label: 'Price Range',
                min: minPrice,
                max: adjustedMaxPrice
            }),
            createSelectFilter({
                field: 'unit_type',
                label: 'Unit Type',
                options: uniqueUnitTypes
            }),
            createSelectFilter({
                field: 'apt_type',
                label: 'Apartment Type',
                options: uniqueAptTypes
            }),
            createSelectFilter({
                field: 'floor',
                label: 'Floor',
                options: uniqueFloors,
                valueType: 'number'
            }),
            createSelectFilter({
                field: 'num_rooms',
                label: 'Number of Rooms',
                options: uniqueRooms,
                valueType: 'number'
            }),
            createToggleFilter({
                field: 'sold',
                label: 'Sold units',
                predicate: (unit) => Boolean(unit.settled_price)
            }),
        ];
    }, [data]);

    // Check if data reference has changed
    const dataChanged = prevDataRef.current !== data;
    if (dataChanged) {
        prevDataRef.current = data;
    }

    return (
        <FilterContainer
            filters={filterConfigs}
            data={data}
            onFilterChange={onFilterChange}
            // Only force reset when data reference changes
            key={dataChanged ? Math.random() : 'filter-container'}
        />
    );
};


const Units = () => {
    const dispatch = useDispatch();
    // Data
    const {units, error, loading, isUnitSuccess} = useSelector(selectUnitProperties)
    const [filteredData, setFilteredData] = useState(units);
    const {blocks, isBlockSuccess} = useSelector(selectBlockProperties)
    const {blockId} = useParams();
    // Modal variables
    const [showExportCsvModal, setShowExportCsvModal] = useState(false);
    const {isUnitModalOpen, unitModalType, currentUnit} = useSelector(
        (state) => state.Modal
    );
    const [unitSelectedForDelete, setUnitSelectedForDelete] = useState(null)
    // Other
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const {userProfile, isAdmin} = useProfile();

    useEffect(() => {
        (async () => {
            try {
                await dispatch(onGetBlocks());
            } catch (error) {
                console.error("Failed to fetch blocks: ", error);
            }
        })();
    }, [dispatch]);

    useEffect(() => {
        (async () => {
            try {
                const queryParams = {block: blockId};
                await dispatch(onGetUnits(queryParams));
            } catch (error) {
                console.error("Failed to fetch units: ", error);
            }
        })();
    }, [dispatch, blockId]);

    useEffect(() => {
        if (units && units.length > 0) {
            setFilteredData(units);
        }
    }, [units]);

    // Modal related functions
    const handleCreateUnit = () => {
        dispatch(openUnitModal({
            modalType: 'create',
            unit: null
        }));
    };
    const handleDeleteUnit = (unit) => {
        if (unit) {
            dispatch(onDeleteUnit(unit.id));
            setShowDeleteModal(false);
        }
    };
    const handleCloseModal = () => {
        dispatch(closeUnitModal());
    };

    const columns = useMemo(
        () => [

            {
                header: "Unit Type",
                accessorKey: "unit_type",
                enableColumnFilter: false,
            },
            {
                header: "Apartment Type",
                accessorKey: "apt_type",
                enableColumnFilter: false,
            },
            {
                header: "CF",
                accessorKey: "cf",
                enableColumnFilter: false,
            },
            {
                header: "Number",
                accessorKey: "nr",
                enableColumnFilter: false,

            },
            {
                header: "initial_price",
                accessorKey: "initial_price",
                enableColumnFilter: false,
            },
            {
                header: "Settled Price",
                accessorKey: "settled_price",
                enableColumnFilter: false,
            },
            {
                header: "Floor",
                accessorKey: "floor",
                enableColumnFilter: false,
                cell: (cell) => {
                    const floor = cell.getValue();
                    const rowClass = floor % 2 === 0 ? "even-floor" : "odd-floor";
                    return <div className={rowClass}>{floor}</div>;
                },
            },
            {
                header: "Number OF Rooms",
                accessorKey: "num_rooms",
                enableColumnFilter: false,
            },
            {
                header: "Action",
                cell: (cellProps) => {
                    return (
                        <ul className="list-inline hstack gap-2 mb-0">
                            <li className="list-inline-item">
                                <Link
                                    to={`/unit-details/${cellProps.row.original.id}`}
                                    className="text-primary d-inline-block"
                                >
                                    <i className="ri-eye-fill fs-16"></i>
                                </Link>
                            </li>
                            {isAdmin && (
                                <>
                                    <li className="list-inline-item">
                                        <Link
                                            to="#"
                                            className="text-danger d-inline-block remove-item-btn"
                                            onClick={() => {
                                                const data = cellProps.row.original;
                                                setUnitSelectedForDelete(data)
                                                setShowDeleteModal(true)
                                            }}
                                        >
                                            <i className="ri-delete-bin-5-fill fs-16"></i>
                                        </Link>
                                    </li>
                                </>
                            )}
                        </ul>
                    );
                },
            },
        ],
        [units]
    );

    const handleFilterChange = useCallback((newFilteredData) => {
        if (Array.isArray(newFilteredData)) {
            setFilteredData(newFilteredData);
        }
    }, []);

    return (
        <div className="page-content">
            <ExportCSVModal
                show={showExportCsvModal}
                onCloseClick={() => setShowExportCsvModal(false)}
                data={units}
            />
            <Container fluid>
                <BreadCrumb title={`Block ${blocks?.find(block => block.id === parseInt(blockId))?.name || ''}`} pageTitle="Units" />
                {isUnitSuccess && units && units.length > 0 && (
                    <UnitFilters
                        data={units}
                        onFilterChange={handleFilterChange}
                    />
                )}
                <Row>
                    <Col lg={12}>
                        <Card id="orderList">
                            <CardHeader className="border-0">
                                <Row className="align-items-center gy-3">
                                    <div className="col-sm">
                                        <h5 className="card-title mb-0">{name}</h5>
                                    </div>
                                    <div className="col-sm-auto" style={{marginBottom: "10px"}}>
                                        <div className="d-flex gap-1 flex-wrap">
                                            {isAdmin &&
                                                <button
                                                    type="button"
                                                    className="btn btn-success add-btn"
                                                    id="create-btn"
                                                    onClick={handleCreateUnit}
                                                >
                                                    <i className="ri-add-line align-bottom me-1"></i>Create Unit
                                                </button>}
                                            <button type="button" className="btn btn-info" onClick={() => setShowExportCsvModal(true)}>
                                                <i className="ri-file-download-line align-bottom me-1"></i>Export
                                            </button>
                                        </div>
                                    </div>
                                </Row>
                            </CardHeader>

                            <CardBody className="pt-0">
                                <div>
                                    {isUnitSuccess ? (
                                        units && units.length > 0 ? (
                                            <TableContainer
                                                columns={columns}
                                                data={filteredData || []}
                                                // isGlobalFilter={true}
                                                customPageSize={20}
                                                divClass="table-responsive table-card mb-1"
                                                tableClass="align-middle table-nowrap"
                                                theadClass="table-light text-muted text-uppercase"
                                                // handleUnitClick={handleUnitClick}
                                                isUnitFilter={true}
                                                SearchPlaceholder="Search for any field"
                                            />
                                        ) : (
                                            <div className="text-center mt-5">
                                                <p className="text-muted">No units are introduced yet. Please add a new unit.</p>
                                            </div>
                                        )
                                    ) : (
                                        <Loader error={error}/>
                                    )}

                                </div>
                                    <ToastContainer closeButton={false} limit={1}/>
                                    <UnitModal
                                                show={isUnitModalOpen}
                                                onCloseClick={handleCloseModal}
                                                unit={undefined}
                                                blocks={blocks}
                                    />
                                <DeleteModal
                                        show={showDeleteModal}
                                        onDeleteClick={() => handleDeleteUnit(unitSelectedForDelete)}
                                        onCloseClick={() => setShowDeleteModal(false)}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    )

}

export default Units

